import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useRef,
} from 'react';
import { ArrowUp } from 'lucide-react';

import { useComposerTextarea } from 'modules/ai/hooks/useComposerTextarea';

import './ComposerField.scss';

export type ComposerFieldVariant = 'agent' | 'embed';

export type ComposerFieldProps = {
  value: string;
  onChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit: () => void;
  placeholder: string;
  disabled?: boolean;
  maxHeight?: number;
  autoFocus?: boolean;
  leadingSlot?: ReactNode;
  variant?: ComposerFieldVariant;
  sendAriaLabel?: string;
  inputTestId?: string;
  sendTestId?: string;
};

const ComposerField: FC<ComposerFieldProps> = ({
  value,
  onChange,
  onSubmit,
  placeholder,
  disabled = false,
  maxHeight = 200,
  autoFocus = false,
  leadingSlot,
  variant = 'agent',
  sendAriaLabel = 'Send',
  inputTestId,
  sendTestId,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useComposerTextarea({ textareaRef, value, maxHeight });

  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    if (variant === 'agent' && !disabled) {
      textareaRef.current?.focus();
    }
  }, [disabled, variant]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onSubmit();
    }
  };

  const canSend = value.trim().length > 0 && !disabled;

  const rootClassName =
    variant === 'embed'
      ? 'navi-composer navi-composer--embed'
      : 'navi-composer';

  return (
    <div className={rootClassName}>
      {leadingSlot}
      <textarea
        ref={textareaRef}
        className="navi-composer__input"
        value={value}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        rows={1}
        data-testid={inputTestId}
      />
      <button
        type="button"
        className={`navi-composer__send${
          canSend ? ' navi-composer__send--active' : ''
        }`}
        onClick={onSubmit}
        disabled={!canSend}
        aria-label={sendAriaLabel}
        data-testid={sendTestId}
      >
        <ArrowUp />
      </button>
    </div>
  );
};

export default ComposerField;
